import defaultConfig from './config';

class GetFtr {
    #externalEndpoint = '/action/getFtrEntitlement';
    #internalEndpoint = '/action/getFtrInternalEntitlement';

    constructor(externalButtonsSelector, internalButtonsSelector, config = defaultConfig) {
        this.config = config;
        this.externalButtons = this.#curateButtons(externalButtonsSelector);
        this.internalButtons = this.#curateButtons(internalButtonsSelector);
    }

    initialize() {
        this.#createObservers(this.externalButtons, this.#externalEndpoint);
        this.#createObservers(this.internalButtons, this.#internalEndpoint);
    }

    #createObservers(buttons, endpoint) {
        if (!buttons) return;
        const dois = Object.keys(buttons);
        const observerIndex = new WeakMap();
        const callback = async ([entry], observer) => {
            if (entry.intersectionRatio > 0 || entry.isIntersecting) {
                observer.disconnect(); // Don't move me down. Otherwise, nasty race conditions will occur. Fuck around and find out at your own risk.
                if (entry.target.classList.contains(this.config.buttons.class)) return;
                const batchIndex = observerIndex.get(observer);
                const batchDois = dois.slice(this.#getBatchLowerBound(batchIndex), this.#getBatchUpperBound(batchIndex, dois.length));
                try {
                    const entitlementsResponse = await this.#fetchEntitlements(batchDois, endpoint);
                    entitlementsResponse.entitlements.forEach(entitlement => this.#updateButton(buttons[entitlement.doi], entitlement));
                } catch (e) {
                    console.error(`Failed to get GetFTR entitlements for ${batchDois}: `, e);
                }
            }
        };

        const observers = Array.from({length: Math.ceil(dois.length / this.config.batchSize)},
            () => new IntersectionObserver(callback, {
                root: null,
                rootMargin: '100%',
                threshold: 0
            })
        );

        observers.forEach((observer, batchIndex) => {
            for (let i = this.#getBatchLowerBound(batchIndex); i < this.#getBatchUpperBound(batchIndex, dois.length); i++) {
                const button = buttons[dois[i]];
                observer.observe(button);
            }
            observerIndex.set(observer, batchIndex);
        });
    }

    #curateButtons(selector) {
        if (!selector) return;
        return Array.from(document.querySelectorAll(selector)).reduce((acc, button) => {
            const a = button.firstChild;
            a.innerText = this.config.labels.button;
            const doi = a.href.match(this.config.buttons.doiRegex);
            if (doi) {
                acc[decodeURIComponent(doi[0])] = button;
            }
            return acc;
        }, {});
    }

    #getBatchLowerBound(batchIndex) {
        return batchIndex * this.config.batchSize;
    }

    #getBatchUpperBound(batchIndex, maxBatchUpperBound) {
        return Math.min((batchIndex + 1) * this.config.batchSize, maxBatchUpperBound);
    }

    async #fetchEntitlements(dois, endpoint) {
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('dois', dois.join(','));
        const response = await fetch(url, {signal: AbortSignal.timeout(5000)});
        return await response.json();
    }

    #updateButton(button, entitlement) {
        const anchor = button.querySelector('a');
        button.classList.add(this.config.buttons.class);
        entitlement.document && anchor.setAttribute('href', entitlement.document);
        if (['yes', 'maybe'].includes(entitlement.entitled)) {
            // TODO: refactor and rename .icon-getFTR-na and .icon-getFTR-av
            this.#insertIndicator(anchor, 'icon-getFTR-na');
            let labelKey = entitlement.entitled === 'yes' ? entitlement.accessType : 'maybe';
            if (labelKey === 'paid' && entitlement.internal) {
                labelKey = 'session';
            }
            anchor.setAttribute('title', this.config.labels.access[labelKey]);
        } else if (entitlement.av) {
            this.#insertIndicator(anchor, 'icon-getFTR-av');
            anchor.setAttribute('title', this.config.labels.access.av);
            anchor.setAttribute('href', entitlement.av[0].url);
        }
    }

    #insertIndicator(anchor, indicatorClass) {
        const indicator = document.createElement('i');
        indicator.classList.add(indicatorClass);
        indicator.setAttribute('aria-hidden', 'true');
        anchor.prepend(indicator);
    }
}
export default GetFtr;